/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import first from 'lodash/first';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { gnoment } from 'shared-ui/gnoment';
import {
    Alert,
    Button,
    DisplayHtmlContent,
    FlexCol,
    FlexRow,
    Heading,
    MediaGallery,
    ScrollToHelper,
    SecurityHelper,
} from 'sarsaparilla';

import { INVENTORY_PERMIT } from 'ui-field-sales/src/constants/types';
import GettingHereShared from 'ui-permit/shared-components/venuePage/GettingHereShared';
import VenueTabWrapper from 'ui-permit/shared-components/venuePage/VenueTabWrapper';
import { isSeasonOpen } from 'ui-permit/shared-components/utils/permit-utilsNew';
import { mapAndSortLotteries } from 'ui-permit/shared-components/utils/lotteryUtilsNew';
import {
    CellSummaryContainer,
    RatingSummaryContainer,
} from 'ui-rating-review/src/containers/AggregateBar';
import { EASTERN_NECK_ID, MATTAMUSKEET_YOUTH_WATERFOWL } from 'ui-permit/src/constants';
import RightRailContainer from 'ui-permit/shared-components/venuePage/RightRail';
import DivisionsMapContainer from 'ui-permit/shared-components/venuePage/DivisionsMapContainer';
import { SharedVenuePage } from 'shared-ui/components/SharedVenuePage/SharedVenuePage';
import { authModalActions } from 'shared-ui/auth/redux';
import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl';
import { useSendGAEvent } from 'shared-ui/utils/gaCommon';
import { availabilityPageRoute } from '../../dev/routesArray';
import {
    availabilityActions,
    lotteryActions,
    permitActions,
    seasonActions,
} from '../actions';
import {
    availabilitySelectors,
    lotterySelectors,
    permitSelectors,
    seasonSelectors,
} from '../reducers';
import { canSelectDate, isOpenPermitType } from '../utils';
import { LOCATION_TZ, permitTypes } from '../constants/hunting-permit-constants';
import { UNLIMITED_PERMITS_THRESHOLD } from '../constants/shared-constants.js';
import DateSelector from '../components/DateSelector';
import GroupSizeSelector from '../components/GroupSizeSelector';
import HuntType from '../components/HuntType';
import VenueDates from '../components/VenueDates';
import { readMaxGroupSizeRule } from '../constants/registration';

function HuntingVenuePage({
    fetchSeasons,
    fetchPermit,
    fetchAvailability,
    fetchLotteries,
    permit,
    inventoryAvailable,
    ratingReviews,
    seasons,
    lotteries,
    openLoginModal,
    dates,
    availability,
    error,
    lotteriesError,
}) {
    const [searchParams] = useSearchParams();
    const activeTabSearchParam = searchParams.get('activeTab');

    const [dateState, setDateState] = useState('');
    const [huntTypeState, setHuntTypeState] = useState('');
    const [groupSizeState, setGroupSizeState] = useState(1);
    const [isLoadingPermitContentState, setIsLoadingPermitContentState] = useState(true);
    const [activeTabIndexState, setActiveTabIndexState] = useState(() => {
        return activeTabSearchParam ? parseInt(activeTabSearchParam, 10) : 0;
    });
    const [shouldScrollToImportantDatesState, setShouldScrollToImportantDatesState] =
        useState(false);

    const navigate = useNavigate();
    const { permitId } = useParams();

    const {
        id,
        name: title,
        recarea_name: recareaName,
        latitude,
        longitude,
        recarea: recareaID,
        permit_types_list: permitTypesList,
        rules,
    } = permit;
    const {
        description,
        season_information,
        lottery_information,
        quota_details,
        driving_directions: drivingDirections,
        need_to_know_permit: needToKnowPermit,
        fee_policy: feePolicy,
        cancellation_policy: cancellationPolicy,
        no_show_policy: noShowPolicy,
        discounts,
    } = permit.description;

    useEffect(() => {
        if (shouldScrollToImportantDatesState && activeTabIndexState === 0) {
            const el = document.getElementById('important-dates');
            const header = document.querySelector('header');
            const currentPosition = window.scrollY;
            const y =
                el.getBoundingClientRect().top - header.offsetHeight + currentPosition;
            ScrollToHelper.scrollToY(y);
            el.focus();
            setShouldScrollToImportantDatesState(false);
        }
    }, [shouldScrollToImportantDatesState, activeTabIndexState]);

    const handleImportantDatesClick = useCallback(() => {
        setShouldScrollToImportantDatesState(true);
        setActiveTabIndexState(0);
    }, [setShouldScrollToImportantDatesState, setActiveTabIndexState]);

    const handleOnActiveTabIndexChange = (newActiveTabIndex) => {
        setActiveTabIndexState(newActiveTabIndex);
    };

    const handleChangeHuntType = (huntType) => {
        const currentSeason = first(seasons);
        setHuntTypeState(huntType);
        setDateState('');

        if (currentSeason) {
            fetchAvailability({
                permitId,
                type: huntType,
                year: currentSeason.season_year,
            }).then(() => {
                if (dates.length === 1) {
                    setDateState(dates[0]);
                }
            });
        }
    };

    const setHuntingType = () => {
        const huntType = permit?.permit_types_list?.[0]?.id;
        if (
            huntType === permitTypes.WATERFOWL_HUNT ||
            huntType === permitTypes.LAKE_LANIER_HUNT ||
            huntType === permitTypes.ALLATOONA_HUNT
        ) {
            handleChangeHuntType(huntType);
        }
    };

    // Fire the view_item GA Event
    useSendGAEvent({
        itemLevel: 'Facility',
        facilityId: permitId,
        itemCategory: INVENTORY_PERMIT,
        isDataLoading: !isLoadingPermitContentState,
    });

    useEffect(() => {
        const currentDate = gnoment.tz(LOCATION_TZ[permitId]).format('YYYY-MM-DD');

        const nonLotteryPromises = Promise.all([
            fetchPermit(permitId),
            fetchSeasons({ permitId, currentDate }),
        ]).then(() => setHuntingType());

        Promise.all([nonLotteryPromises, fetchLotteries(permitId)]).finally(() => {
            setIsLoadingPermitContentState(false);
        });
    }, []);

    const canBookFunc = () => {
        const permitsAvailable = inventoryAvailable(
            huntTypeState,
            canSelectDate(permit, huntTypeState),
            dateState
        );
        const hasPermitsRemaining = isOpenPermitType(permit, huntTypeState)
            ? permitsAvailable
            : true;

        const canBook = !!(
            huntTypeState &&
            (!canSelectDate(permit, huntTypeState) ? true : dateState) &&
            groupSizeState &&
            hasPermitsRemaining
        );
        return canBook;
    };

    const isInSeasonCheck = () => {
        const firstDate = calcFirstDate();
        const issuanceDate = !canSelectDate(permit, huntTypeState)
            ? firstDate
            : dateState || firstDate;

        for (let i = 0; i < seasons.length; i++) {
            const season = seasons[i];
            if (
                season.type !== 'Lottery' &&
                season.start_date <= issuanceDate &&
                season.end_date >= issuanceDate
            ) {
                return true;
            }
        }
        return false;
    };

    const calcFirstDate = () => {
        return dates?.find((d) => /\//.test(d)) ? dates[0].split('/')[0] : dates[0];
    };

    const getNonLotteryRightRailComponents = () => {
        if (!id) return null;

        const cantSelectDate = !canSelectDate(
            { permit_types_list: permitTypesList },
            huntTypeState
        );
        const permitsAvailable = inventoryAvailable(
            huntTypeState,
            !cantSelectDate,
            dateState
        );
        const canBook = canBookFunc();
        const isInSeason = isInSeasonCheck();

        const firstDate = calcFirstDate();
        const dateRoute = cantSelectDate ? firstDate : dateState || firstDate;
        const maxGroupSize = readMaxGroupSizeRule(id, huntTypeState, rules);

        const hideGroupSizeSelector = [
            // hide group size selector for these permits in the Available Permits section on the Prev Venue Page
            MATTAMUSKEET_YOUTH_WATERFOWL,
        ].includes(permit.id);

        const hideGroupSize =
            maxGroupSize === 1 || !permitsAvailable || hideGroupSizeSelector;

        // TODO: hunting permits does not distinguish between a permit type that has no inventory for a season
        // vs one that will release later in the season. Hardcoding primitive hunt for Eastern Neck as closed for 2023 season
        const permitClosed =
            huntTypeState === permitTypes.PRIMITIVE && id === EASTERN_NECK_ID;
        const displayAvailabilityRange = permit?.components?.display_availability_range;

        return (
            <div
                key="nonLotteryRightRailComponents"
                className="rec-venue-page-form-wrapper"
            >
                <fieldset>
                    <div className="rec-form-item-wrap-full">
                        {huntTypeState === permitTypes.WATERFOWL_HUNT ? (
                            <p>
                                Please check the important dates section to find when
                                certain hunt dates go on sale.
                            </p>
                        ) : (
                            <p>
                                Please select a hunt type below. Limited hunts have a
                                limited amount of availability per unit during the season.
                            </p>
                        )}
                        <HuntType
                            permitTypesList={permitTypesList}
                            permitID={permit.id}
                            value={huntTypeState}
                            onChange={handleChangeHuntType}
                        />
                    </div>

                    {huntTypeState && (
                        <FlexRow>
                            <FlexCol xs={12} xxl={hideGroupSize ? 12 : 6}>
                                <div className="rec-form-item-wrap-full">
                                    <DateSelector
                                        isTextOnly={cantSelectDate}
                                        dates={dates}
                                        value={dateState}
                                        onChange={(v) => setDateState(v)}
                                        permitClosed={permitClosed}
                                        displayAvailabilityRange={
                                            displayAvailabilityRange
                                        }
                                        availability={availability}
                                    />
                                </div>
                            </FlexCol>
                            {!hideGroupSize && (
                                <FlexCol xs={12} xxl={6}>
                                    <GroupSizeSelector
                                        permit={permit}
                                        permitType={huntTypeState}
                                        value={groupSizeState}
                                        onChange={(v) => setGroupSizeState(v)}
                                    />
                                </FlexCol>
                            )}
                        </FlexRow>
                    )}

                    {huntTypeState &&
                        typeof permitsAvailable !== 'undefined' &&
                        permitsAvailable <= UNLIMITED_PERMITS_THRESHOLD &&
                        isInSeason && (
                            <div
                                className="rec-form-item-wrap-full mt-2"
                                aria-live="polite"
                            >
                                <Alert
                                    type="info"
                                    heading={`${permitsAvailable} permits remaining`}
                                    hasAriaLive
                                />
                            </div>
                        )}
                    {huntTypeState &&
                        typeof permitsAvailable !== 'undefined' &&
                        !isInSeason && (
                            <div
                                className="rec-form-item-wrap-full mt-2"
                                aria-live="polite"
                            >
                                <Alert
                                    type="error"
                                    heading={`This permit is not yet released. Please check the location page for more information.`}
                                    hasAriaLive
                                />
                            </div>
                        )}

                    <div className="rec-form-item-wrap-full mt-2">
                        <Button
                            shouldFitContainer
                            appearance="primary"
                            size="md"
                            aria-label="Book Now"
                            isDisabled={!canBook || !isInSeason}
                            to={
                                canBook && isInSeason
                                    ? availabilityPageRoute(
                                          id,
                                          huntTypeState,
                                          dateRoute,
                                          `${groupSizeState}`
                                      )
                                    : ''
                            }
                        >
                            Book Now
                        </Button>
                    </div>
                </fieldset>
            </div>
        );
    };

    const getBreadcrumbsPath = () => {
        const path = [{ url: '/', title: 'Home' }];
        const recAreaName = get(permit, 'recarea_name');
        const recAreaUrl = get(permit, 'recarea');

        if (recAreaName && recAreaUrl) {
            // TODO: may have to retrieve this from data object
            // I think there are some locations/gateways that aren’t camping
            path.push({ url: getGatewayUrl(recAreaUrl), title: recAreaName });
        }

        path.push({ title: get(permit, 'name', '').trim() });
        return path;
    };

    const enterLottery = (reservationId, lotteryId) => {
        const redirectURL = `/permits/${permit.id}/lottery/${lotteryId}/registration/${reservationId}`;
        const loggedIn = SecurityHelper.isLoggedIn();
        if (!loggedIn) {
            openLoginModal();
            return;
        }
        navigate(redirectURL);
    };

    const renderRightRail = () => {
        const isOtherSeasonOpen = isSeasonOpen(seasons);
        const { openLotteries, futureLotteries: futureLotteriesList } =
            mapAndSortLotteries({ lotteries, permitTimeZone: permit.time_zone });

        const handleImportantDates =
            !isEmpty(permit?.important_dates) && handleImportantDatesClick;

        return RightRailContainer({
            permit,
            isClosed: !isOtherSeasonOpen,
            isLoading: isLoadingPermitContentState,
            openLotteries,
            futureLotteriesList,
            handleImportantDates,
            handleEnterLottery: enterLottery,
            children: isOtherSeasonOpen ? [getNonLotteryRightRailComponents()] : null,
        });
    };

    const renderContent = (body, titleHeading) => {
        if (body) {
            return (
                <>
                    {titleHeading && (
                        <Heading appearance="h5" headingLevel={2}>
                            {titleHeading}
                        </Heading>
                    )}
                    <DisplayHtmlContent html={body} />
                </>
            );
        }
        return null;
    };

    const renderOverview = () => {
        return (
            <VenueTabWrapper permitId={permitId}>
                {renderContent(description)}
                {renderContent(season_information, 'Permit & Season Information')}
                <VenueDates permit={permit} />
                {renderContent(lottery_information, 'Lottery Information')}
                {renderContent(quota_details, 'How Does the Quota Work for this Permit?')}
                <MediaGallery assetId={permitId} />
            </VenueTabWrapper>
        );
    };

    const renderNeedToKnow = () => {
        return (
            <VenueTabWrapper permitId={permitId}>
                {renderContent(needToKnowPermit)}
            </VenueTabWrapper>
        );
    };

    const renderPolicy = () => {
        return (
            <VenueTabWrapper permitId={permitId}>
                {renderContent(feePolicy, 'Fee Policies')}
                {renderContent(cancellationPolicy, 'Cancellation & Change Policies')}
                {noShowPolicy && renderContent(noShowPolicy, 'No Show Policies')}
                {discounts && renderContent(discounts, 'Discount Pass Policies')}
            </VenueTabWrapper>
        );
    };

    const renderContactInformationTab = () => {
        const address1 = get(permit, 'addresses[0].address1');
        const address2 = get(permit, 'addresses[0].address2');
        const address3parts = [
            get(permit, 'addresses[0].city'),
            get(permit, 'addresses[0].state'),
            get(permit, 'addresses[0].zip_code'),
        ];
        const description1 = get(permit, 'addresses[0].description1');
        const description2 = get(permit, 'addresses[0].description2');
        const remarks = get(permit, 'addresses[0].remarks');
        let address3;

        if (address3parts[0] && address3parts[1] && address3parts[2]) {
            address3 = `${address3parts[0]}, ${address3parts[1]} ${address3parts[2]}`;
        }

        return (
            <VenueTabWrapper permitId={permitId}>
                {permit.addresses && (
                    <div>
                        <div className="permit-venue-row">
                            <div className="permit-venue-left-col">
                                <Heading
                                    headingLevel={2}
                                    appearance="h5"
                                    className="mb-2"
                                >
                                    Mailing Address
                                </Heading>
                            </div>
                            <div className="permit-venue-right-col" translate="no">
                                {description1 && <div>{description1}</div>}
                                {description2 && <div>{description2}</div>}
                                {address1 && <div>{address1}</div>}
                                {address2 && <div>{address2}</div>}
                                {address3 && <div>{address3}</div>}
                            </div>
                        </div>
                        <div className="permit-venue-row">
                            <div className="permit-venue-left-col">
                                <Heading
                                    headingLevel={2}
                                    appearance="h5"
                                    className="mb-2"
                                >
                                    Phone Number
                                </Heading>
                            </div>
                            <div className="permit-venue-right-col">
                                <p>{permit.phone}</p>
                                {remarks && <div>{remarks}</div>}
                            </div>
                        </div>
                    </div>
                )}
            </VenueTabWrapper>
        );
    };

    const tabsToShow = () => {
        if (isLoadingPermitContentState) return [];
        return [
            {
                label: 'Overview',
                content: renderOverview(),
            },
            {
                label: 'Need to Know',
                content: renderNeedToKnow(),
            },
            {
                label: 'Fees & Cancellations',
                content: renderPolicy(),
            },
            {
                label: 'Getting Here',
                content: (
                    <GettingHereShared
                        permitId={permitId}
                        longitude={longitude}
                        latitude={latitude}
                        drivingDirections={drivingDirections}
                    />
                ),
            },
            {
                label: 'Contacts',
                content: renderContactInformationTab(),
            },
        ];
    };

    const errorList = [error || lotteriesError];
    let partOfUrl = `/permits/${id}`;

    if (recareaName && recareaID) {
        partOfUrl = getGatewayUrl(recareaID);
    }
    const ratingsPresent = get(ratingReviews, 'number_of_ratings', 0) !== 0;
    const cellCoveragePresent = !isNil(
        get(ratingReviews, 'aggregate_cell_coverage_ratings')
    );
    return (
        <div className="hunting-permit-venue-page">
            <SharedVenuePage
                isLoading={isLoadingPermitContentState}
                map={
                    <DivisionsMapContainer
                        permit={permit}
                        permitType="hunting-permit"
                        fitFeatures
                    />
                }
                errors={errorList.filter((err) => err)}
                title={title || ''}
                assetId={permitId}
                favoriteEntityId={permitId}
                favoriteEntityType={'permit'}
                inventoryType="Permit"
                breadcrumbPath={getBreadcrumbsPath()}
                activeTabIndex={activeTabIndexState}
                onActiveTabIndexChange={handleOnActiveTabIndexChange}
                partOf={{
                    title: recareaName || title,
                    url: partOfUrl,
                }}
                starRating={
                    ratingsPresent && (
                        <RatingSummaryContainer locationType="Permit" locationId={id} />
                    )
                }
                cellRating={
                    cellCoveragePresent && (
                        <CellSummaryContainer locationType="Permit" locationId={id} />
                    )
                }
                tabs={tabsToShow()}
                rightRail={renderRightRail()}
                mapLatitude={latitude}
                mapLongitude={longitude}
            />
        </div>
    );
}

HuntingVenuePage.propTypes = {
    dates: PropTypes.array,
    fetchAvailability: PropTypes.func,
    fetchPermit: PropTypes.func,
    fetchLotteries: PropTypes.func,
    fetchSeasons: PropTypes.func,
    inventoryAvailable: PropTypes.func,
    openLoginModal: PropTypes.func,
    permit: PropTypes.object /*HuntingPermitType*/,
    lotteries: PropTypes.array /*LotteryType[]*/,
    error: PropTypes.string,
    lotteriesError: PropTypes.string,
    availability: PropTypes.object,
    ratingReviews: PropTypes.object,
};

function mapDispatchToProps(dispatch) {
    return {
        fetchPermit: bindActionCreators(permitActions.fetchPermit, dispatch),
        fetchAvailability: bindActionCreators(
            availabilityActions.fetchAvailability,
            dispatch
        ),
        fetchLotteries: bindActionCreators(lotteryActions.fetchLotteries, dispatch),
        fetchSeasons: bindActionCreators(seasonActions.fetchSeasons, dispatch),
        openLoginModal: bindActionCreators(authModalActions.openLoginModal, dispatch),
    };
}

export default connect((state) => {
    return {
        permit: permitSelectors.getPermit(state),
        inventoryAvailable: availabilitySelectors.getInventoryAvailable(state),
        dates: availabilitySelectors.getDates(state),
        lotteries: lotterySelectors.getLotteries(state),
        seasons: seasonSelectors.getSeasons(state),
        error: permitSelectors.getPermitError(state),
        lotteriesError: lotterySelectors.getLotteriesError(state),
        availability: availabilitySelectors.getAvailability(state),
        ratingReviews: state.ratingReviews.fetchAggregate.data,
    };
}, mapDispatchToProps)(HuntingVenuePage);

// cSpell:ignore recarea, LANIER, ALLATOONA
